import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'

Vue.use(VueRouter)

// 全局变量 - 页面停留时长统计相关
let pageStartTime = null // 页面进入时间戳
let currentRouteName = null // 当前路由名称
let currentFullPath = null // 当前路由完整路径
let pageViewId = null // 页面访问唯一标识（用于追踪同一次访问）
let isPageVisible = true // 页面可见状态


/**
 * 生成唯一标识符
 * @returns {string} 唯一ID
 */
 function generateUniqueId() {
  return 'pv_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
}


/**
 * 获取当前用户信息
 * @returns {Object} 用户信息对象
 */
function getUserInfo() {
  try {
    // 从Vuex获取用户信息
    if (store && store.state && store.state.user) {
      return {
        userId: store.state.user.id || 'anonymous',
        username: store.state.user.username || '匿名用户'
      }
    }
    // 从localStorage获取用户信息
    const userStr = localStorage.getItem('user')
    if (userStr) {
      return JSON.parse(userStr)
    }
  } catch (error) {
    console.error('获取用户信息失败:', error)
  }
  return { userId: 'anonymous', username: '匿名用户' }
}

/**
 * 获取设备信息
 * @returns {Object} 设备信息对象
 */
function getDeviceInfo() {
  return {
    userAgent: navigator.userAgent,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    language: navigator.language,
    platform: navigator.platform
  }
}


const routes = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/Home.vue')
  },
  //esim列表
  {
    path: '/products/esim',
    name: 'ESIM',
    component: () => import('@/views/products/ESIM.vue')
  },
  //sim列表页
  {
    path: '/products/sim',
    name: 'SIM',
    component: () => import('@/views/products/SIM.vue')
  },
  {
    path: '/products/detail',
    name: 'ProductDetail',
    component: () => import('@/views/products/detail/esimProductDetail.vue')
  },
  //主页home 区域产品
  {
    path: '/products/groupProductList',
    name: 'GroupProductList',
    component: () => import('@/views/products/detail/groupProductList.vue'),
    meta: {
      activeMenu: '/products/esim' 
    }
  },
  //sim卡列表
  {
    path: '/products/sim/groupProductList',
    name: 'SimGroupProductList',
    component: () => import('@/views/products/detail/simGroupProductList.vue'),
    meta: {
      activeMenu: '/products/sim' 
    }
  },
  //订单支付
  {
    path: '/order/simOrder',
    name: 'SimOrder',
    component: () => import('@/views/order/simOrder.vue'),
    meta: {
      activeMenu: '/products/sim' 
    }
  },
  {
    path: '/cart',
    name: 'Cart',
    component: () => import('@/views/cart/Cart.vue')
  },
  {
    path: '/user',
    name: 'User',
    component: () => import('@/views/user/User.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: 'orders',
        name: 'Orders',
        component: () => import('@/views/user/Orders.vue')
      },
      {
        path: 'card',
        name: 'Cards',
        component: () => import('@/views/user/Cards.vue')
      },
      {
        path: 'orderDetail',
        name: 'OrderDetail',
        component: () => import('@/views/user/OrderDetail.vue')
      },
      {
        path: 'profile',
        name: 'Profile',
        component: () => import('@/views/user/Profile.vue')
      },
      {
        path: 'productBuy',
        name: 'ProductBuy',
        component: () => import('@/views/user/ProductBuy.vue')
      }
    ]
  },
  {
    path: '/support',
    name: 'Support',
    component: () => import('@/views/support/Support.vue'),
    children: [
      {
        path: 'faq',
        name: 'FAQ',
        component: () => import('@/views/support/FAQ.vue')
      },
      
      {
        path: 'how-to-work',
        name: 'HowToWork',
        component: () => import('@/views/support/HowToWork.vue')
      },
      {
        path: 'track',
        name: 'Track',
        component: () => import('@/views/support/Track.vue')
      }
    ]
  },
  {
    path: '/card/renewal',
    name: 'Renewal',
    component: () => import('@/views/web/index.vue')
  },
  {
    path: '/support/return',
    name: 'Return',
    component: () => import('@/views/support/Return.vue')
  },
  {
    path: '/business',
    name: 'Business',
    component: () => import('@/views/business/Business.vue')
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  }
})

// 路由守卫
router.beforeEach((to, from, next) => {
  // 1. 处理上一个页面的停留时长统计 只有在正常导航（非刷新）且有上一个页面时才上报
  if (from.name && pageStartTime) {
    const duration = calculateStayDuration()
    const pageViewData = buildPageViewData(from.name,from.fullPath, duration, false)
    reportPageView(pageViewData, false)
    console.log(`页面${from.name}停留时长: ${(duration / 1000).toFixed(1)}秒`)
  }
  // 2. 初始化新页面的统计信息
  if (to.name) {
    pageStartTime = Date.now()
    currentRouteName = to.name
    currentFullPath = to.fullPath
    pageViewId = generateUniqueId()
    isPageVisible = true // 重置页面可见状态
    console.log(`开始统计页面${to.name}的停留时长`)
  }
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const isAuthenticated = store.getters.isAuthenticated
  // 3. 处理权限验证（如果需要）
  if (requiresAuth && !isAuthenticated) {
    next('/login')
  } else {
    // 4. 继续路由导航
    next()
  }
  
})
// 全局后置钩子 - 页面切换后执行
router.afterEach((to,from)=>{
    // 尝试上报本地存储的待上报数据
    flushPendingData()
})


/**
 * 本地存储待上报数据
 * @param {string} type 数据类型
 * @param {Object} data 数据内容
 */
 function savePendingData(type, data) {
  try {
    const pendingKey = `pending_${type}_data`
    const pendingData = JSON.parse(localStorage.getItem(pendingKey) || '[]')
    pendingData.push(data)
    // 限制存储数量，避免localStorage溢出
    if (pendingData.length > 50) {
      pendingData.splice(0, pendingData.length - 50)
    }
    localStorage.setItem(pendingKey, JSON.stringify(pendingData))
    console.debug('待上报数据已本地存储:', pendingKey)
  } catch (error) {
    console.error('本地存储待上报数据失败:', error)
  }
}

/**
 * 尝试上报本地存储的待上报数据
 */
 function flushPendingData() {
  try {
    const pendingPageViews = JSON.parse(localStorage.getItem('pending_pageView_data') || '[]')
    if (pendingPageViews.length > 0) {
      console.debug('尝试上报待上报页面访问数据:', pendingPageViews.length)
      let url = process.env.VUE_APP_BASE_URL + process.env.VUE_APP_BASE_API;
      fetch(url+'/visitorLog/reportVisitorView/batch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(pendingPageViews),
        credentials: 'include'
      }).then(response => {
        if (response.ok) {
          localStorage.removeItem('pending_pageView_data')
        } else {
          console.error('批量上报待上报数据失败:', response.status)
        }
      }).catch(error => {
        console.error('批量上报待上报数据异常:', error)
      })
    }
  } catch (error) {
    console.error('处理待上报数据异常:', error)
  }
}

/**
 * 计算页面停留时长
 * @returns {number} 停留时长（毫秒）
 */
 function calculateStayDuration() {
  if (!pageStartTime) return 0
  return Date.now() - pageStartTime
}
/**
 * 构建页面访问数据
 * @param {string} pageName 页面名称
 * @param {number} duration 停留时长
 * @param {boolean} isFinal 是否为最终上报（页面关闭时）
 * @returns {Object} 页面访问数据
 */
 function buildPageViewData(pageName,fullPath, duration, isFinal = false) {

  const userInfo = getUserInfo()
  const deviceInfo = getDeviceInfo()
  
  return {
    pageViewId: pageViewId || generateUniqueId(),
    pageName: pageName,
    fullPath: fullPath,
    startTime: new Date(pageStartTime).toISOString(),
    endTime: new Date().toISOString(),
    duration: duration,
    isFinal: isFinal,
    user: userInfo,
    device: deviceInfo,
    referrer: document.referrer,
    url: window.location.href,
    query: window.location.search,
    hash: window.location.hash,
    timestamp: new Date().toISOString()
  }
}

/**
 * 上报页面访问数据
 * @param {Object} data 页面访问数据
 * @param {boolean} isFinal 是否为最终上报
 */
 function reportPageView(data, isFinal = false) {
  if (!data.pageName || data.duration < 100) {
    console.debug('跳过无效的页面访问数据上报:', data)
    return
  }
  try {
    let url = process.env.VUE_APP_BASE_URL + process.env.VUE_APP_BASE_API;
    // 1. 优先使用sendBeacon（页面卸载时更可靠）
    if (navigator.sendBeacon) {
      const success = navigator.sendBeacon(
        url+'/visitorLog/reportVisitorView',
        new Blob([JSON.stringify(data)], { type: 'application/json' })
      )
      if (success) {
        console.debug('sendBeacon数据上报成功')
        return
      }
      console.debug('sendBeacon数据上报失败，回退到fetch')
    }
    // 2. sendBeacon失败时使用fetch with keepalive
    fetch(url+'/visitorLog/reportVisitorView', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(data),
      keepalive: true, // 关键：确保页面卸载时请求能完成
      credentials: 'include' // 包含cookie，用于用户标识
    }).then(response => {
      if (response.ok) {
        console.debug('fetch数据上报成功')
      } else {
        console.error('fetch数据上报失败:', response.status)
        // 上报失败时，本地存储数据，下次有机会再上报
        savePendingData('pageView', data)
      }
    }).catch(error => {
      console.error('fetch数据上报异常:', error)
      savePendingData('pageView', data)
    })
  } catch (error) {
    console.error('数据上报异常:', error)
    savePendingData('pageView', data)
  }
}

/**
 * 处理页面可见性变化
 */
 function handleVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    // 页面变为不可见时，暂停计时并上报中间数据
    if (currentRouteName && pageStartTime && isPageVisible) {
      const duration = calculateStayDuration()
      const pageViewData = buildPageViewData(currentRouteName,currentFullPath, duration, false)
      reportPageView(pageViewData, false)
      isPageVisible = false
      console.debug('页面变为不可见，暂停计时并上报中间数据')
    }
  } else {
    // 页面变为可见时，重新开始计时
    if (currentRouteName && !isPageVisible) {
      pageStartTime = Date.now()
      isPageVisible = true
      console.debug('页面变为可见，重新开始计时')
    }
  }
}

/**
 * 处理页面卸载  
 * 1 触发beforeunload事件（执行handleBeforeUnload）
 * 2 卸载当前页面
 * 3 重新加载页面
 */
 function handleBeforeUnload() {
  if (currentRouteName && pageStartTime) {
    const duration = calculateStayDuration()
    const pageViewData = buildPageViewData(currentRouteName, duration, true)
    reportPageView(pageViewData, true)
    console.debug('页面即将卸载，上报最终停留时长数据')
  }
}

// 绑定页面可见性变化事件
document.addEventListener('visibilitychange', handleVisibilityChange)

// 绑定页面卸载事件
window.addEventListener('beforeunload', handleBeforeUnload)

// 页面加载完成后尝试上报待上报数据
window.addEventListener('load', () => {
  setTimeout(flushPendingData, 3000) // 延迟3秒执行，确保页面完全加载
})

export default router 